import React, { useMemo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import PerfectScrollbar from 'react-perfect-scrollbar'

import { SignatureWizardSteps, removeFile, reset, setActiveStep, setFile, setIsFinished } from '@/redux/ducks/signature/edit'
import DocumentPreview from '@/modules/signature/components/document-preview/signature-document-preview'
import SignatureDocumentSelection from '@/modules/signature/components/wizard/signature-document/signature-document-selection'
import SignatureForm from '@/modules/signature/components/wizard/signature-form/signature-form'
import SignaturePlacement from '@/modules/signature/components/wizard/signature-placement/signature-placement'
import SignatureWizardActions from './signature-wizard-actions'
import ThemeInterface from '@/theme-interface'
import { Signer } from '@/modules/signature/types'
import { Document } from '@/modules/document/types'
import { useAppSelector } from '@/redux/store'
import { FILE_LIST_FILTER } from '@/modules/base/util/constants'

export const signatureWizardScrollElId = 'perfect-scroll-signature-wizard'

type SignatureWizardProps = {
  tenantCollaborationId: string,
  styles?: { root: string, content: string, footer: string },
  document?: Document,
  onAbort?: () => void,
  onFinish?: (data: {
    signers: Signer[],
    message: string,
    dueDate: string,
    file: Document,
    tenantCollaborationId?: string
  }) => void
  filter?: keyof typeof FILE_LIST_FILTER
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    overflow: 'auto',
    width: '100%',
    height: '100%',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: 500,
  },
  documentPreview: {
    overflow: 'auto',
  },
  content: {
    overflow: 'auto',
    minWidth: 497,
    height: '100%',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    background: theme.backgroundColor,
    height: 80,
    width: '100%',
    padding: '0px 30px',
  },
}), { name: 'SignatureWizard' })

const SignatureWizard = (props: SignatureWizardProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { tenantCollaborationId, styles, onFinish, onAbort, filter } = props
  const {
    activeStep,
    file,
    signers,
    dueDate,
    message,
    isFinished,
  } = useAppSelector(state => state.signature.edit)
  const rootClass = clsx(classes.root, styles?.root && styles.root)
  const contentClass = clsx(classes.content, styles?.content && styles.content)
  const footerClass = clsx(classes.footer, styles?.footer && styles.footer)

  useEffect(() => {
    if (props.document) {
      dispatch(setFile({ file: { ...props.document, source: 'container' } }))
      dispatch(setActiveStep({ step: SignatureWizardSteps.form }))
    }
  }, [props.document])

  useEffect(() => {
    return () => {
      dispatch(reset())
    }
  }, [])

  useEffect(() => {
    if (!isFinished) {
      return
    }

    // there can only be one tenantCollaboration per todo
    onFinish?.({
      file,
      signers,
      dueDate,
      message,
      tenantCollaborationId: signers.find((signer) => signer.tenantCollaborationId)?.tenantCollaborationId,
    })
    dispatch(setIsFinished(false))
  }, [isFinished])

  const handleFileSelected = (file) => {
    dispatch(setFile({ file }))
    dispatch(setActiveStep({ step: SignatureWizardSteps.form }))
  }
  const handleRemoveFile = () => {
    dispatch(reset())
    dispatch(removeFile())
    dispatch(setActiveStep({ step: SignatureWizardSteps.documentSelection }))
  }

  const content = useMemo(() => {
    switch (activeStep) {
      case SignatureWizardSteps.documentSelection:
        return <SignatureDocumentSelection tenantCollaborationId={tenantCollaborationId} onFileSelected={handleFileSelected} filter={filter} />
      case SignatureWizardSteps.form:
        return <SignatureForm tenantCollaborationId={tenantCollaborationId} />
      case SignatureWizardSteps.signaturePlacement:
        return <SignaturePlacement />
      default:
        return null
    }

  }, [activeStep])
  const footer = useMemo(() => {
    if ([SignatureWizardSteps.documentSelection].includes(activeStep)) {
      return null
    }

    return (
      <div className={footerClass}>
        <SignatureWizardActions onAbort={onAbort} />
      </div>
    )
  }, [activeStep, signers, dueDate, message])

  return (
    <div className={rootClass}>
      {file && (
        <div className={classes.documentPreview}>
          <DocumentPreview
            auth
            file={file}
            onRemoveFile={handleRemoveFile} />
        </div>
      )}
      <div className={classes.contentWrapper}>
        <PerfectScrollbar id={signatureWizardScrollElId} className={contentClass}>
          {content}
        </PerfectScrollbar>
        {footer}
      </div>
    </div>
  )
}

export default SignatureWizard
